import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MainLayout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MessageSquare, User, RefreshCw, Phone, Clock, Bot, Search,
  AlertTriangle, ChevronLeft, Users, Shield, ShieldOff, Inbox,
  Smartphone, Monitor, ArrowUp, Send, UserCheck, Power, Pause, Loader2, FileText, PlusCircle, X, ChevronDown,
  Bell, BellOff, Volume2, VolumeX,
  Image, Mic, Video, MapPin, Sticker, Download, Play,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const POLL_INTERVAL = 10000;
const ORIGINAL_TITLE = 'Hit Grill';

const STATUS_CONFIG = {
  HUMAN_ACTIVE: { label: 'Humano', color: 'bg-red-500 text-white', dot: 'bg-red-500' },
  BOT_FREE: { label: 'Bot Livre', color: 'bg-emerald-500 text-white', dot: 'bg-emerald-500' },
  TTL_EXPIRED: { label: 'Expirado', color: 'bg-zinc-400 text-white', dot: 'bg-zinc-400' },
};

const SENDER_CONFIG = {
  CLIENT: { label: 'Cliente', bubbleCls: 'bg-white text-zinc-900 border border-zinc-200', align: 'justify-start', labelCls: 'text-zinc-400' },
  BOT: { label: 'Bot', bubbleCls: 'bg-emerald-600 text-white', align: 'justify-end', labelCls: 'text-emerald-200' },
  HUMAN_WEB: { label: 'Humano (Web)', bubbleCls: 'bg-blue-600 text-white', align: 'justify-end', labelCls: 'text-blue-200' },
  HUMAN_ANDROID: { label: 'Humano (Cel)', bubbleCls: 'bg-indigo-600 text-white', align: 'justify-end', labelCls: 'text-indigo-200' },
  MANUAL_INBOX: { label: 'Operador', bubbleCls: 'bg-violet-600 text-white', align: 'justify-end', labelCls: 'text-violet-200' },
};

// ==================== NOTIFICATION SOUND ====================
function createNotificationSound() {
  let ctx = null;
  return {
    play: () => {
      try {
        if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
        // Double beep (WhatsApp style)
        const now = ctx.currentTime;
        [0, 0.15].forEach(offset => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(880, now + offset);
          gain.gain.setValueAtTime(0.3, now + offset);
          gain.gain.exponentialRampToValueAtTime(0.01, now + offset + 0.12);
          osc.start(now + offset);
          osc.stop(now + offset + 0.12);
        });
      } catch (e) { /* silent fail */ }
    }
  };
}
const notifSound = createNotificationSound();

// ==================== HELPERS ====================
function formatTTL(seconds) {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s < 10 ? '0' : ''}${s}s`;
}

function formatBytes(bytes) {
  if (!bytes || isNaN(bytes) || bytes === 0) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const tz = 'America/Sao_Paulo';
  const nowBR = new Date().toLocaleDateString('pt-BR', { timeZone: tz });
  const dateBR = d.toLocaleDateString('pt-BR', { timeZone: tz });
  const isToday = dateBR === nowBR;
  if (isToday) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: tz }) + ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
}

function formatTimeAgo(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

// ==================== SUMMARY CARDS ====================
function SummaryCards({ summary }) {
  if (!summary) return null;
  const cards = [
    { label: 'Monitorados', value: summary.total_contacts, icon: Users, bg: 'bg-zinc-50', text: 'text-zinc-700' },
    { label: 'Humano Ativo', value: summary.human_active, icon: User, bg: 'bg-red-50', text: 'text-red-600' },
    { label: 'Bot Livre', value: summary.bot_free, icon: Bot, bg: 'bg-emerald-50', text: 'text-emerald-600' },
    { label: 'TTL Expirado', value: summary.ttl_expired, icon: Clock, bg: 'bg-amber-50', text: 'text-amber-600' },
  ];
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="inbox-summary-cards">
      {cards.map((c, i) => (
        <Card key={i} className={`${c.bg} border-0 shadow-sm`}>
          <CardContent className="p-2.5 flex items-center gap-2">
            <c.icon className={`w-4 h-4 ${c.text} flex-shrink-0`} />
            <div>
              <p className={`text-xl font-bold ${c.text} leading-none`}>{c.value ?? '—'}</p>
              <p className="text-[10px] text-zinc-500">{c.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ==================== CONVERSATION LIST ====================
function ConversationList({ conversations, activeJid, onSelect, loading, unreadMap, globalSearch, onGlobalSearch }) {
  const [search, setSearch] = useState(globalSearch || '');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (onGlobalSearch) onGlobalSearch(val);
  };

  const filtered = conversations.filter(c => {
    if (statusFilter !== 'all' && c.status_atual !== statusFilter) return false;
    // Client-side filter as fallback (server-side already filters)
    if (search) {
      const s = search.toLowerCase();
      return (c.name || '').toLowerCase().includes(s) ||
        (c.contact_jid || '').includes(s) ||
        (c.phone_display || '').includes(s);
    }
    return true;
  });

  // Sort: unread first, then by last_message_at DESC (estilo WhatsApp)
  const sorted = [...filtered].sort((a, b) => {
    const ua = unreadMap[a.contact_jid] || 0;
    const ub = unreadMap[b.contact_jid] || 0;
    if (ua > 0 && ub === 0) return -1;
    if (ua === 0 && ub > 0) return 1;
    // Ordenar por last_message_at DESC (conversa mais recente no topo)
    const ta = a.last_message_at ? new Date(typeof a.last_message_at === 'number' ? a.last_message_at * 1000 : a.last_message_at).getTime() : 0;
    const tb = b.last_message_at ? new Date(typeof b.last_message_at === 'number' ? b.last_message_at * 1000 : b.last_message_at).getTime() : 0;
    return tb - ta;
  });

  const statusBtns = [
    { key: 'all', label: 'Todos' },
    { key: 'HUMAN_ACTIVE', label: 'Humano' },
    { key: 'BOT_FREE', label: 'Bot' },
    { key: 'TTL_EXPIRED', label: 'Expirado' },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="conversation-list">
      <div className="p-2.5 border-b space-y-2 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input
            placeholder="Buscar nome ou telefone..."
            value={search}
            onChange={handleSearchChange}
            className="pl-8 h-8 text-sm"
            data-testid="inbox-search"
          />
        </div>
        <div className="flex gap-1">
          {statusBtns.map(b => (
            <button
              key={b.key}
              onClick={() => setStatusFilter(b.key)}
              className={`px-2 py-0.5 text-[11px] rounded-full transition-all ${
                statusFilter === b.key
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid={`filter-${b.key}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && sorted.length === 0 ? (
          <div className="p-6 text-center text-zinc-400">
            <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
            <p className="text-xs">Carregando...</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-6 text-center text-zinc-400">
            <Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">Nenhuma conversa</p>
          </div>
        ) : (
          sorted.map(conv => {
            const status = STATUS_CONFIG[conv.status_atual] || STATUS_CONFIG.BOT_FREE;
            const isActive = activeJid === conv.contact_jid;
            const ttl = formatTTL(conv.ttl_remaining_seconds);
            const unread = unreadMap[conv.contact_jid] || 0;

            return (
              <div
                key={conv.contact_jid}
                onClick={() => onSelect(conv)}
                className={`px-2.5 py-2 cursor-pointer border-b border-zinc-50 transition-all ${
                  isActive ? 'bg-blue-50' : unread > 0 ? 'bg-emerald-50/60' : 'hover:bg-zinc-50'
                }`}
                data-testid={`conv-${conv.contact_jid}`}
              >
                <div className="flex items-start gap-2">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-zinc-500" />
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${status.dot}`} />
                    {unread > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid={`unread-badge-${conv.contact_jid}`}>
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className={`font-medium text-[13px] truncate ${unread > 0 ? 'text-zinc-900 font-semibold' : 'text-zinc-900'}`}>{conv.name}</p>
                      <span className="text-[10px] text-zinc-400 flex-shrink-0">
                        {conv.last_message_at ? formatTimestamp(conv.last_message_at) : formatTimeAgo(conv.updated_at)}
                      </span>
                    </div>
                    <p className={`text-[11px] truncate mt-0.5 ${unread > 0 ? 'text-zinc-700 font-medium' : 'text-zinc-500'}`}>
                      {conv.last_message_from_me && <span className="text-zinc-400">Voce: </span>}
                      {conv.last_message_preview || conv.phone_display}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className={`px-1.5 py-0 rounded text-[9px] font-medium ${status.color}`}>
                        {status.label}
                      </span>
                      {ttl && <span className="text-[9px] text-red-500 font-mono">{ttl}</span>}
                      {conv.has_unsure_recent && <AlertTriangle className="w-2.5 h-2.5 text-amber-500" />}
                      {conv.pause_count > 0 && <span className="text-[9px] text-zinc-400">{conv.pause_count}x</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


// ==================== MEDIA BUBBLE (FASE 3) ====================
const mediaCache = {};

function MediaBubble({ msg, contactJid }) {
  const [mediaData, setMediaData] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  const mt = msg.message_type;

  const loadMedia = useCallback(async () => {
    if (!msg.media_has_content || mediaData || mediaLoading) return;
    const cacheKey = msg.message_id;
    if (mediaCache[cacheKey]) { setMediaData(mediaCache[cacheKey]); return; }
    setMediaLoading(true);
    try {
      const phone = contactJid?.split('@')[0] || contactJid;
      const res = await api.get(`/inbox/media/${phone}/${msg.message_id}`);
      if (res.data?.base64) {
        const d = { base64: res.data.base64, mimetype: res.data.mimetype };
        mediaCache[cacheKey] = d;
        setMediaData(d);
      } else {
        setMediaError(true);
      }
    } catch { setMediaError(true); }
    finally { setMediaLoading(false); }
  }, [msg.message_id, msg.media_has_content, contactJid, mediaData, mediaLoading]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioPlaying) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setAudioPlaying(!audioPlaying);
  };

  // IMAGE
  if (mt === 'imageMessage') {
    const src = mediaData ? `data:${mediaData.mimetype || 'image/jpeg'};base64,${mediaData.base64}` : null;
    return (
      <div className="mt-1">
        {src ? (
          <>
            <img src={src} alt="imagem" className="rounded-lg max-w-[240px] max-h-[240px] object-cover cursor-pointer" onClick={() => setShowLightbox(true)} data-testid="media-image" />
            {msg.media_caption && <p className="text-[12px] mt-1 opacity-80">{msg.media_caption}</p>}
            {showLightbox && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowLightbox(false)} data-testid="media-lightbox">
                <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-1" onClick={() => setShowLightbox(false)}><X className="w-5 h-5" /></button>
                <img src={src} alt="imagem" className="max-w-full max-h-full object-contain rounded-lg" />
              </div>
            )}
          </>
        ) : (
          <button onClick={loadMedia} disabled={mediaLoading} className="flex items-center gap-2 px-3 py-2 bg-black/10 rounded-lg text-[12px] hover:bg-black/20 transition-colors" data-testid="media-load-image">
            {mediaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
            {mediaError ? 'Imagem expirada' : mediaLoading ? 'Carregando...' : 'Carregar imagem'}
          </button>
        )}
      </div>
    );
  }

  // AUDIO
  if (mt === 'audioMessage') {
    const duration = msg.media_seconds ? `${Math.floor(msg.media_seconds / 60)}:${String(msg.media_seconds % 60).padStart(2, '0')}` : '';
    const src = mediaData ? `data:${mediaData.mimetype || 'audio/ogg'};base64,${mediaData.base64}` : null;
    return (
      <div className="mt-1">
        {src ? (
          <div className="flex items-center gap-2" data-testid="media-audio-player">
            <button onClick={toggleAudio} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 hover:bg-white/30">
              {audioPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
            </button>
            <div className="flex-1">
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white/60 rounded-full" style={{ width: audioPlaying ? '100%' : '0%', transition: audioPlaying ? `width ${msg.media_seconds || 5}s linear` : 'none' }} />
              </div>
              <span className="text-[10px] opacity-60">{duration}</span>
            </div>
            <audio ref={audioRef} src={src} onEnded={() => setAudioPlaying(false)} preload="auto" />
          </div>
        ) : (
          <button onClick={loadMedia} disabled={mediaLoading} className="flex items-center gap-2 px-3 py-2 bg-black/10 rounded-lg text-[12px] hover:bg-black/20 transition-colors" data-testid="media-load-audio">
            {mediaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
            {mediaError ? 'Audio expirado' : mediaLoading ? 'Carregando...' : `Audio ${duration}`}
          </button>
        )}
      </div>
    );
  }

  // VIDEO
  if (mt === 'videoMessage') {
    const src = mediaData ? `data:${mediaData.mimetype || 'video/mp4'};base64,${mediaData.base64}` : null;
    const duration = msg.media_seconds ? `${Math.floor(msg.media_seconds / 60)}:${String(msg.media_seconds % 60).padStart(2, '0')}` : '';
    return (
      <div className="mt-1">
        {src ? (
          <>
            <video src={src} controls className="rounded-lg max-w-[260px] max-h-[200px]" data-testid="media-video" />
            {msg.media_caption && <p className="text-[12px] mt-1 opacity-80">{msg.media_caption}</p>}
          </>
        ) : (
          <button onClick={loadMedia} disabled={mediaLoading} className="flex items-center gap-2 px-3 py-2 bg-black/10 rounded-lg text-[12px] hover:bg-black/20 transition-colors" data-testid="media-load-video">
            {mediaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
            {mediaError ? 'Video expirado' : mediaLoading ? 'Carregando...' : `Video ${duration}`}
          </button>
        )}
      </div>
    );
  }

  // DOCUMENT
  if (mt === 'documentMessage') {
    const fileName = msg.media_file_name || 'Documento';
    return (
      <div className="mt-1">
        {mediaData ? (
          <a
            href={`data:${mediaData.mimetype || 'application/octet-stream'};base64,${mediaData.base64}`}
            download={fileName}
            className="flex items-center gap-2 px-3 py-2 bg-black/10 rounded-lg text-[12px] hover:bg-black/20 transition-colors"
            data-testid="media-document"
          >
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="truncate flex-1">{fileName}</span>
            <Download className="w-3.5 h-3.5 flex-shrink-0" />
          </a>
        ) : (
          <button onClick={loadMedia} disabled={mediaLoading} className="flex items-center gap-2 px-3 py-2 bg-black/10 rounded-lg text-[12px] hover:bg-black/20 transition-colors" data-testid="media-load-doc">
            {mediaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {mediaError ? 'Documento expirado' : mediaLoading ? 'Carregando...' : fileName}
          </button>
        )}
      </div>
    );
  }

  // STICKER
  if (mt === 'stickerMessage') {
    const src = mediaData ? `data:${mediaData.mimetype || 'image/webp'};base64,${mediaData.base64}` : null;
    return (
      <div className="mt-1">
        {src ? (
          <img src={src} alt="sticker" className="w-24 h-24 object-contain" data-testid="media-sticker" />
        ) : (
          <button onClick={loadMedia} disabled={mediaLoading} className="flex items-center gap-1 px-2 py-1 bg-black/10 rounded text-[11px]" data-testid="media-load-sticker">
            {mediaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sticker className="w-3 h-3" />}
            Sticker
          </button>
        )}
      </div>
    );
  }

  // LOCATION
  if (mt === 'locationMessage' && msg.media_lat && msg.media_lon) {
    const mapsUrl = `https://www.google.com/maps?q=${msg.media_lat},${msg.media_lon}`;
    return (
      <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-2 px-3 py-2 bg-black/10 rounded-lg text-[12px] hover:bg-black/20 transition-colors" data-testid="media-location">
        <MapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
        <span>{msg.media_location_name || 'Ver no mapa'}</span>
      </a>
    );
  }

  // REACTION
  if (mt === 'reactionMessage') {
    return null; // Reactions are emojis, already in text
  }

  return null;
}



// ==================== TEMPLATE DROPDOWN ====================
function TemplateDropdown({ contact, onSessionChange }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/inbox/templates');
      setTemplates(res.data?.templates || []);
    } catch { setTemplates([]); }
  };

  const handleOpen = () => {
    if (!open) loadTemplates();
    setOpen(!open);
  };

  const handleSend = async (tmpl) => {
    if (!contact || sending) return;
    setSending(true);
    try {
      const phone = contact.contact_jid?.split('@')[0] || contact.contact_jid;
      const name = contact.name || contact.contact_name || 'Cliente';
      const bodyComp = (tmpl.components || []).find(c => c.type === 'BODY');
      const varCount = (bodyComp?.text?.match(/\{\{\d+\}\}/g) || []).length;
      const params = varCount > 0 ? [name.split(' ')[0]] : [];
      while (params.length < varCount) params.push('-');
      const res = await api.post('/inbox/conversations/' + phone + '/send-template', {
        template_name: tmpl.name,
        params: params,
      });
      if (res.data?.success) {
        toast.success('Template "' + tmpl.name + '" enviado!');
        setOpen(false);
        if (onSessionChange) onSessionChange();
      } else {
        toast.error(res.data?.error || 'Erro ao enviar template');
      }
    } catch (e) {
      toast.error('Falha ao enviar template');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative" ref={dropRef}>
      <Button
        variant="ghost" size="sm"
        className="h-[38px] px-2 text-zinc-500 hover:text-emerald-600"
        onClick={handleOpen}
        title="Enviar template"
        data-testid="template-dropdown-btn"
      >
        <FileText className="w-4 h-4" />
        <ChevronDown className="w-3 h-3 ml-0.5" />
      </Button>
      {open && (
        <div className="absolute bottom-full mb-1 right-0 w-72 bg-white border border-zinc-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="px-3 py-2 border-b border-zinc-100">
            <p className="text-xs font-semibold text-zinc-700">Enviar Template</p>
            <p className="text-[10px] text-zinc-400">Templates aprovados pela Meta</p>
          </div>
          {templates.length === 0 ? (
            <p className="px-3 py-4 text-xs text-zinc-400 text-center">Nenhum template aprovado</p>
          ) : (
            templates.map(t => {
              const body = (t.components || []).find(c => c.type === 'BODY');
              return (
                <button
                  key={t.name}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-zinc-50 transition-colors"
                  onClick={() => handleSend(t)}
                  disabled={sending}
                  data-testid={'template-option-' + t.name}
                >
                  <p className="text-xs font-medium text-zinc-800">{t.name}</p>
                  <p className="text-[10px] text-zinc-400 line-clamp-2 mt-0.5">{body?.text?.substring(0, 80) || ''}</p>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}


// ==================== NEW CONVERSATION BUTTON ====================
function NewConversationButton({ onConversationStarted }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sending, setSending] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    setPhone('');
    setCustomerName('');
    setSelectedTemplate(null);
    try {
      const res = await api.get('/inbox/templates');
      setTemplates(res.data?.templates || []);
    } catch { setTemplates([]); }
  };

  const handleSend = async () => {
    if (!phone.trim() || !selectedTemplate || sending) return;
    setSending(true);
    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const fullPhone = cleanPhone.length <= 11 ? '55' + cleanPhone : cleanPhone;
      const name = customerName.trim() || 'Cliente';
      const bodyComp = (selectedTemplate.components || []).find(c => c.type === 'BODY');
      const varCount = (bodyComp?.text?.match(/\{\{\d+\}\}/g) || []).length;
      const params = varCount > 0 ? [name.split(' ')[0]] : [];
      while (params.length < varCount) params.push('-');
      const res = await api.post('/inbox/conversations/' + fullPhone + '/send-template', {
        template_name: selectedTemplate.name,
        params: params,
      });
      if (res.data?.success) {
        toast.success('Template enviado para ' + fullPhone);
        setOpen(false);
        if (onConversationStarted) onConversationStarted();
      } else {
        toast.error(res.data?.error || 'Erro ao enviar');
      }
    } catch (e) {
      toast.error('Falha ao enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
        onClick={handleOpen}
        data-testid="new-conversation-btn"
      >
        <PlusCircle className="w-3.5 h-3.5 mr-1" />
        Nova Conversa
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-sm font-bold text-zinc-900">Nova Conversa via Template</h2>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-zinc-600 mb-1 block">Telefone do cliente</label>
                <div className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2">
                  <Phone className="w-4 h-4 text-zinc-400" />
                  <input
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="11999999999"
                    className="flex-1 text-sm outline-none"
                    data-testid="new-conv-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600 mb-1 block">Nome (opcional)</label>
                <input
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Nome do cliente"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600 mb-1 block">Escolha o template</label>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {templates.map(t => {
                    const body = (t.components || []).find(c => c.type === 'BODY');
                    const isSelected = selectedTemplate?.name === t.name;
                    return (
                      <button
                        key={t.name}
                        className={'w-full text-left px-3 py-2 rounded-lg border transition-colors ' + (isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 hover:bg-zinc-50')}
                        onClick={() => setSelectedTemplate(t)}
                        data-testid={'new-conv-template-' + t.name}
                      >
                        <p className={'text-xs font-medium ' + (isSelected ? 'text-emerald-700' : 'text-zinc-800')}>{t.name}</p>
                        <p className="text-[10px] text-zinc-400 line-clamp-2 mt-0.5">{body?.text?.substring(0, 100) || ''}</p>
                      </button>
                    );
                  })}
                  {templates.length === 0 && (
                    <p className="text-xs text-zinc-400 text-center py-3">Nenhum template aprovado</p>
                  )}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleSend}
                disabled={!phone.trim() || !selectedTemplate || sending}
                data-testid="new-conv-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Enviar Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ==================== MESSAGE THREAD ====================
function MessageThread({ contact, messages, session, loading, onLoadMore, hasMore, onBack, onSessionChange }) {
  const scrollRef = useRef(null);
  const prevMsgCount = useRef(0);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (scrollRef.current && messages.length !== prevMsgCount.current) {
      if (messages.length <= 50 || messages.length > prevMsgCount.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      prevMsgCount.current = messages.length;
    }
  }, [messages]);

  useEffect(() => {
    setMsgText('');
    setSending(false);
    setActionLoading(null);
  }, [contact?.contact_jid]);

  const isHumanActive = session?.status_atual === 'HUMAN_ACTIVE';

  const handleSend = async () => {
    if (!msgText.trim() || sending || !contact) return;
    setSending(true);
    try {
      const phone = contact.contact_jid?.split('@')[0] || contact.contact_jid;
      const res = await api.post(`/inbox/conversations/${phone}/send`, { text: msgText.trim() });
      if (res.data?.success) {
        toast.success('Mensagem enviada');
        setMsgText('');
        if (onSessionChange) onSessionChange();
      } else {
        toast.error(res.data?.error || 'Erro ao enviar');
      }
    } catch (e) {
      toast.error('Falha ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAction = async (action) => {
    if (actionLoading || !contact) return;
    setActionLoading(action);
    try {
      const phone = contact.contact_jid?.split('@')[0] || contact.contact_jid;
      let res;
      if (action === 'takeover') {
        res = await api.post(`/inbox/conversations/${phone}/takeover`);
      } else if (action === 'release') {
        res = await api.post(`/inbox/conversations/${phone}/release`);
      } else if (action === 'pause') {
        res = await api.post(`/inbox/conversations/${phone}/pause`, { minutes: 30 });
      }
      if (res?.data?.success) {
        const labels = { takeover: 'Conversa assumida', release: 'Bot liberado', pause: 'Bot pausado 30min' };
        toast.success(labels[action]);
        if (onSessionChange) onSessionChange();
      } else {
        toast.error(res?.data?.error || 'Erro na acao');
      }
    } catch (e) {
      toast.error('Falha na operacao');
    } finally {
      setActionLoading(null);
    }
  };

  if (!contact) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50" data-testid="inbox-empty-state">
        <div className="text-center">
          <MessageSquare className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
          <p className="text-zinc-500 font-medium text-sm">Selecione uma conversa</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">Escolha um contato para ver mensagens</p>
        </div>
      </div>
    );
  }

  const status = session ? (STATUS_CONFIG[session.status_atual] || STATUS_CONFIG.BOT_FREE) : null;
  const ttl = session ? formatTTL(session.ttl_remaining || 0) : null;

  const orderedMessages = [...messages].reverse();

  const groupedByDate = [];
  let currentDate = null;
  for (const msg of orderedMessages) {
    const d = msg.timestamp ? new Date(msg.timestamp * 1000) : null;
    const dateKey = d ? d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : 'Sem data';
    if (dateKey !== currentDate) {
      currentDate = dateKey;
      groupedByDate.push({ type: 'date', date: dateKey });
    }
    groupedByDate.push({ type: 'msg', data: msg });
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="message-thread">
      {/* Header with controls */}
      <div className="bg-white border-b px-3 py-2 flex items-center gap-2 flex-shrink-0">
        <Button variant="ghost" size="sm" className="md:hidden p-1 h-7 w-7" onClick={onBack}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-zinc-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-sm text-zinc-900 truncate">{contact.contact_name || contact.name}</h3>
            {status && (
              <span className={`px-1.5 py-0 rounded text-[9px] font-medium flex-shrink-0 ${status.color}`}>
                {status.label}
              </span>
            )}
            {isHumanActive && ttl && (
              <span className="text-[9px] text-red-500 font-mono flex-shrink-0">{ttl}</span>
            )}
          </div>
          <p className="text-[11px] text-zinc-400 flex items-center gap-1">
            <Phone className="w-2.5 h-2.5" />
            {contact.phone_display}
            {session && session.pause_count > 0 && <span>| {session.pause_count} pausas</span>}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0" data-testid="conversation-controls">
          {!isHumanActive ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px] text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => handleAction('takeover')}
              disabled={!!actionLoading}
              data-testid="takeover-btn"
            >
              {actionLoading === 'takeover' ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3 mr-1" />}
              Assumir
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[11px] text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => handleAction('release')}
                disabled={!!actionLoading}
                data-testid="release-btn"
              >
                {actionLoading === 'release' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Power className="w-3 h-3 mr-1" />}
                Liberar Bot
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[11px] text-amber-600 border-amber-200 hover:bg-amber-50"
                onClick={() => handleAction('pause')}
                disabled={!!actionLoading}
                data-testid="pause-btn"
              >
                {actionLoading === 'pause' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pause className="w-3 h-3 mr-1" />}
                Pausar 30m
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-zinc-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            <span className="text-sm">Carregando mensagens...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-zinc-400">
            <MessageSquare className="w-6 h-6 mr-2 opacity-50" />
            <span className="text-sm">Nenhuma mensagem encontrada</span>
          </div>
        ) : (
          <div className="max-w-xl mx-auto">
            {hasMore && (
              <div className="text-center py-2">
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onLoadMore} data-testid="load-more-btn">
                  <ArrowUp className="w-3 h-3 mr-1" /> Carregar anteriores
                </Button>
              </div>
            )}
            {groupedByDate.map((item, idx) => {
              if (item.type === 'date') {
                return (
                  <div key={`date-${idx}`} className="flex justify-center my-3">
                    <span className="bg-zinc-200 text-zinc-600 text-[10px] px-2.5 py-0.5 rounded-full">
                      {item.date}
                    </span>
                  </div>
                );
              }
              const msg = item.data;
              const sender = SENDER_CONFIG[msg.sender_type] || SENDER_CONFIG.CLIENT;
              const isClient = msg.sender_type === 'CLIENT';

              return (
                <div key={msg.message_id || idx} className={`flex ${sender.align} mb-1.5`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 shadow-sm ${sender.bubbleCls} ${
                    isClient ? 'rounded-bl-sm' : 'rounded-br-sm'
                  }`}>
                    {!isClient && (
                      <div className={`text-[9px] mb-0.5 flex items-center gap-0.5 ${sender.labelCls}`}>
                        {msg.sender_type === 'BOT' && <Bot className="w-2.5 h-2.5" />}
                        {msg.sender_type === 'HUMAN_ANDROID' && <Smartphone className="w-2.5 h-2.5" />}
                        {msg.sender_type === 'HUMAN_WEB' && <Monitor className="w-2.5 h-2.5" />}
                        {msg.sender_type === 'MANUAL_INBOX' && <UserCheck className="w-2.5 h-2.5" />}
                        {sender.label}
                      </div>
                    )}
                    {isClient && msg.push_name && (
                      <p className="text-[9px] text-zinc-400 mb-0.5 font-medium">{msg.push_name}</p>
                    )}
                    <p className="text-[13px] whitespace-pre-wrap break-words leading-snug">{msg.text}</p>
                    {msg.message_type && msg.message_type !== 'conversation' && msg.message_type !== 'extendedTextMessage' && (
                      <MediaBubble msg={msg} contactJid={contact?.contact_jid} />
                    )}
                    <p className={`text-[9px] mt-0.5 ${isClient ? 'text-zinc-400' : 'opacity-60'}`}>
                      {formatTimestamp(msg.timestamp)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Message input footer */}
      <div className="bg-white border-t px-3 py-2 flex-shrink-0" data-testid="message-input-area">
        <div className="flex items-end gap-2">
          <textarea
            value={msgText}
            onChange={e => setMsgText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite uma mensagem..."
            rows={1}
            className="flex-1 resize-none border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 max-h-24 min-h-[38px]"
            style={{ height: 'auto', minHeight: '38px', maxHeight: '96px' }}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'; }}
            disabled={sending}
            data-testid="message-input"
          />
          <TemplateDropdown contact={contact} onSessionChange={onSessionChange} />
          <Button
            size="sm"
            className="h-[38px] px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleSend}
            disabled={!msgText.trim() || sending}
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        {isHumanActive && (
          <p className="text-[10px] text-red-500 mt-1 flex items-center gap-0.5">
            <UserCheck className="w-2.5 h-2.5" />
            Controle humano ativo {ttl && <span className="font-mono">({ttl})</span>}
          </p>
        )}
        {!isHumanActive && (
          <p className="text-[10px] text-zinc-400 mt-1 flex items-center gap-0.5">
            <Bot className="w-2.5 h-2.5" />
            Bot ativo — ao enviar, o bot sera pausado automaticamente
          </p>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function WhatsAppInboxPage() {
  const [summary, setSummary] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  const searchTimeoutRef = useRef(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [msgPage, setMsgPage] = useState(1);
  const [msgPages, setMsgPages] = useState(1);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [mobileShowThread, setMobileShowThread] = useState(false);

  // Notification state
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('inbox_sound') !== 'off'; } catch { return true; }
  });
  const [unreadMap, setUnreadMap] = useState({});
  const lastSeenRef = useRef({}); // { jid: lastMessageTimestamp }
  const pollRef = useRef(null);
  const msgPollRef = useRef(null);
  const titleIntervalRef = useRef(null);
  const totalUnread = Object.values(unreadMap).reduce((a, b) => a + b, 0);

  // Persist sound preference
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('inbox_sound', next ? 'on' : 'off'); } catch {}
      if (next) {
        notifSound.play(); // test beep on enable
        toast.success('Notificacoes sonoras ativadas');
      } else {
        toast('Notificacoes sonoras desativadas');
      }
      return next;
    });
  }, []);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Title flash effect
  useEffect(() => {
    if (titleIntervalRef.current) clearInterval(titleIntervalRef.current);
    if (totalUnread > 0) {
      let toggle = false;
      titleIntervalRef.current = setInterval(() => {
        document.title = toggle ? `(${totalUnread}) Nova mensagem!` : ORIGINAL_TITLE;
        toggle = !toggle;
      }, 1500);
    } else {
      document.title = ORIGINAL_TITLE;
    }
    return () => {
      if (titleIntervalRef.current) clearInterval(titleIntervalRef.current);
      document.title = ORIGINAL_TITLE;
    };
  }, [totalUnread]);

  const fireNotification = useCallback((convName, preview) => {
    if (soundEnabled) notifSound.play();
    // Browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(convName || 'Nova mensagem', {
          body: preview || 'Mensagem recebida',
          icon: '/favicon.ico',
          tag: 'inbox-msg',
          renotify: true,
        });
      } catch {}
    }
  }, [soundEnabled]);

  const fetchList = useCallback(async (searchTerm = '') => {
    try {
      const params = { limit: 100 };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
        params.limit = 200;
      }
      const [sumRes, convRes] = await Promise.all([
        api.get('/inbox/summary'),
        api.get('/inbox/conversations', { params }),
      ]);
      if (sumRes.data && !sumRes.data.error) setSummary(sumRes.data);
      if (convRes.data && convRes.data.conversations) {
        const convs = convRes.data.conversations;
        setConversations(convs);

        // Detect new client messages
        const newUnread = { ...unreadMap };
        let hasNew = false;

        for (const c of convs) {
          const jid = c.contact_jid;
          const lastTs = c.last_message_at || 0;
          const prevTs = lastSeenRef.current[jid];

          // New message from client (not from_me)
          if (prevTs !== undefined && lastTs > prevTs && !c.last_message_from_me) {
            const count = (newUnread[jid] || 0) + 1;
            newUnread[jid] = count;
            hasNew = true;
            fireNotification(c.name, c.last_message_preview);
          }

          // Update last seen (only if we've initialized)
          if (prevTs !== undefined) {
            lastSeenRef.current[jid] = lastTs;
          }
        }

        // First load: initialize timestamps without triggering notifications
        if (Object.keys(lastSeenRef.current).length === 0) {
          for (const c of convs) {
            lastSeenRef.current[c.contact_jid] = c.last_message_at || 0;
          }
        }

        if (hasNew) setUnreadMap(newUnread);
      }
      setLastUpdate(new Date());
    } catch (e) {
      console.error('[Inbox] Fetch error:', e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireNotification]);

  const fetchMessages = useCallback(async (jid, pg = 1, append = false) => {
    if (!jid) return;
    setMsgsLoading(true);
    try {
      const res = await api.get(`/inbox/conversations/${jid}/messages`, {
        params: { page: pg, limit: 50 }
      });
      if (res.data && !res.data.error) {
        if (append) {
          setMessages(prev => [...(res.data.messages || []), ...prev]);
        } else {
          setMessages(res.data.messages || []);
        }
        setSession(res.data.session);
        setContactInfo({ contact_name: res.data.contact_name, phone_display: res.data.phone_display });
        setMsgPages(res.data.pages || 1);
      }
    } catch (e) {
      console.error('[Inbox] Messages error:', e);
    } finally {
      setMsgsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
    pollRef.current = setInterval(fetchList, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchList]);

  useEffect(() => {
    if (msgPollRef.current) clearInterval(msgPollRef.current);
    if (activeConv) {
      msgPollRef.current = setInterval(() => {
        fetchMessages(activeConv.contact_jid, 1);
      }, POLL_INTERVAL);
    }
    return () => { if (msgPollRef.current) clearInterval(msgPollRef.current); };
  }, [activeConv, fetchMessages]);

  const handleSelect = (conv) => {
    setActiveConv(conv);
    setMessages([]);
    setMsgPage(1);
    setMobileShowThread(true);
    fetchMessages(conv.contact_jid, 1);
    // Clear unread for this conversation
    setUnreadMap(prev => {
      const next = { ...prev };
      delete next[conv.contact_jid];
      return next;
    });
  };

  const handleLoadMore = () => {
    if (msgPage < msgPages && activeConv) {
      const nextPage = msgPage + 1;
      setMsgPage(nextPage);
      fetchMessages(activeConv.contact_jid, nextPage, true);
    }
  };

  const handleBack = () => {
    setMobileShowThread(false);
  };

  const handleSessionChange = () => {
    if (activeConv) {
      fetchMessages(activeConv.contact_jid, 1);
    }
    fetchList();
  };

  return (
    <MainLayout>
      <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }} data-testid="whatsapp-inbox-page">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              {totalUnread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center animate-bounce">
                  {totalUnread > 99 ? '99' : totalUnread}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-base font-bold text-zinc-900 leading-none">Inbox WhatsApp</h1>
              <p className="text-[10px] text-zinc-400">
                Tempo real
                {totalUnread > 0 && <span className="text-emerald-600 font-medium ml-1">{totalUnread} nova{totalUnread > 1 ? 's' : ''}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {summary?.config && (
              <div className="hidden md:flex items-center gap-2 text-[10px] text-zinc-400 mr-1">
                {summary.config.enabled ? (
                  <span className="flex items-center gap-0.5"><Shield className="w-2.5 h-2.5" /> Ativa</span>
                ) : (
                  <span className="flex items-center gap-0.5"><ShieldOff className="w-2.5 h-2.5" /> Inativa</span>
                )}
                <span>TTL: {summary.config.ttl_minutes}min</span>
              </div>
            )}
            {/* Sound toggle */}
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 w-7 p-0 ${soundEnabled ? 'text-emerald-600' : 'text-zinc-400'}`}
              onClick={toggleSound}
              title={soundEnabled ? 'Desativar som' : 'Ativar som'}
              data-testid="sound-toggle-btn"
            >
              {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            </Button>
            {lastUpdate && (
              <span className="text-[10px] text-zinc-400" data-testid="last-update">
                {formatTimeAgo(lastUpdate.toISOString())}
              </span>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={fetchList} data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <NewConversationButton onConversationStarted={() => fetchList()} />
          </div>
        </div>

        {/* Summary cards */}
        <div className="px-3 py-2 border-b bg-white flex-shrink-0">
          <SummaryCards summary={summary} />
        </div>

        {/* Main split layout */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left: Conversation list */}
          <div className={`w-full md:w-[340px] lg:w-[380px] md:flex-shrink-0 border-r bg-white ${
            mobileShowThread ? 'hidden md:flex md:flex-col' : 'flex flex-col'
          }`}>
            <ConversationList
              conversations={conversations}
              globalSearch={globalSearch}
              onGlobalSearch={(term) => {
                setGlobalSearch(term);
                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = setTimeout(() => fetchList(term), 400);
              }}
              activeJid={activeConv?.contact_jid}
              onSelect={handleSelect}
              loading={loading}
              unreadMap={unreadMap}
            />
          </div>

          {/* Right: Message thread */}
          <div className={`flex-1 min-w-0 ${!mobileShowThread ? 'hidden md:block' : 'block'}`}>
            <MessageThread
              contact={activeConv ? { ...activeConv, ...contactInfo } : null}
              messages={messages}
              session={session}
              loading={msgsLoading}
              onLoadMore={handleLoadMore}
              hasMore={msgPage < msgPages}
              onBack={handleBack}
              onSessionChange={handleSessionChange}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
